/**
 * Журнал видалених документів (2026-08-09, рішення власника).
 *
 * Видалення документів ФІЗИЧНЕ — «показати видалені» у списках неможливе.
 * Джерело: core.audit_log (action=DELETE) — тригер зберігає повний знімок
 * документа (before_data): №, дата, контрагент, сума + хто/коли видалив.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { api } from '@/api/client';
import { orgs } from '@/api/catalogs';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';

interface DeletedDoc {
  occurred_at: string;
  user_email: string | null;
  entity_type: string;
  document_number: string | null;
  document_date: string | null;
  contractor_name: string | null;
  total_amount: string | null;
  comment: string | null;
  was_posted: boolean | null;
}

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DeletedDocsJournalPage() {
  const [orgId, setOrgId] = useOrgFilter();
  const { dateFrom, dateTo } = useDefaultPeriod();
  const [typeFilter, setTypeFilter] = useState('');
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });

  const { datasource, total } = useInfiniteGridDatasource<DeletedDoc>(
    (limit, offset) => api.get<{ items: DeletedDoc[]; total: number; limit: number; offset: number }>(
      '/documents/deleted-journal', {
        params: {
          limit, offset,
          document_type: typeFilter || undefined,
          organization_id: orgId || undefined,
          date_from: dateFrom, date_to: dateTo,
        },
      }).then((r) => r.data),
    [typeFilter, orgId, dateFrom, dateTo],
  );

  const cols = useMemo<ColDef<DeletedDoc>[]>(() => [
    {
      field: 'occurred_at', headerName: 'Коли видалено', width: 170, sortable: false,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'user_email', headerName: 'Хто видалив', width: 220, sortable: false,
      valueFormatter: (p) => p.data ? (p.value ?? 'система') : '',
    },
    {
      field: 'entity_type', headerName: 'Документ', width: 220, sortable: false,
      valueFormatter: (p) => p.value ? (DOC_TABLE_NAMES_UK[p.value] ?? p.value) : '',
    },
    { field: 'document_number', headerName: '№', width: 140, sortable: false },
    {
      field: 'document_date', headerName: 'Дата документа', width: 140, sortable: false,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    { field: 'contractor_name', headerName: 'Контрагент', flex: 1, minWidth: 200, sortable: false },
    {
      field: 'total_amount', headerName: 'Сума', width: 130, sortable: false,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'was_posted', headerName: 'Був проведений', width: 140, sortable: false,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.data ? (p.value ? 'так' : 'ні') : '',
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Видалені документи<TitleTotal total={total} /></h2>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 240 }}>
          <option value="">Усі організації</option>
          {(orgsQ.data?.items ?? []).map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          style={{ maxWidth: 280 }}>
          <option value="">Усі види документів</option>
          {Object.entries(DOC_TABLE_NAMES_UK).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <span style={{ color: 'var(--muted)', fontSize: '0.82rem', alignSelf: 'center' }}>
          період — з фільтра у шапці
        </span>
      </div>
      <InfiniteDocGrid<DeletedDoc>
        datasource={datasource}
        columnDefs={cols}
        storageKey="deleted-docs-journal"
      />
    </div>
  );
}
